/* eslint-disable react/prop-types */
import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import {
  Form,
  Spin,
  Row,
  Col,
  Input,
  Button,
  Space,
  Grid,
  message,
  Result,
  Progress,
  DatePicker,
  Typography,
} from 'antd';
import { getTramitesIndexados } from 'api/tramites/tramites';
import { useSelector } from 'react-redux';
import { isAdmin } from 'utils/permission';
import { capitalize, toMoment, toUpper } from 'utils/normalizers';
import Table from 'components/Table';
import Select from 'components/Select';
import { DeleteOutlined, FilterOutlined, InboxOutlined } from '@ant-design/icons';
import { getContenTypePorTipoDePadron, getTipoDePadronPorContenType } from 'api/catalogos';
import { getDepartamentos } from 'api/configuracion/departamentos';
import { getDependencias } from 'api/configuracion/dependencias';
import padronToString from 'utils/padronesToString';
import { getPasosDeTramites, getTiposDePasosDeTramites, getPlazosMaximos } from 'api/tramites';
import ModalFirmarTramites from 'views/Tramites/components/ModalFirmarTramites';
import { tiposDePasosEnum } from 'views/Tramites/GPM/enums';
import { useHistory } from 'react-router';
import { getHistorialDeTramite } from 'api/tramites/historial';
import { DATE_FORMAT } from 'utils/formatValues';
import { getVehiculoCaja } from 'api/recaudacion';
import LabelValue from 'components/LabelValue';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { getUsuarios } from 'api/usuarios/usuarios';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { InputSearchPro } from 'components/InputSearch';
import { getPadronesMap } from 'api/recaudacion/utils';

const { Text } = Typography;

export const permissionConfig = {
  mainPermission: 'viewcrud_tramite',
  required: [
    'view_tramite',
    'view_unidadoperativa',
    'view_unidadresponsable',
    'view_pasoparatramite',
    'view_tipodepasodeltramite',
    'view_plazomaximo',
    'view_historialdetramite',
    'view_padron',
  ],
  customActions: [
    {
      name: 'firmar',
      title: 'Firma Electrónica',
      permissions: [
        'change_tramite',
        'add_historialdetramite',
        'add_comentariodeltramite',
      ],
    },
  ],
};

const estadosGlobalesDeTramites = [
  {
    id: 2,
    descripcion: 'En proceso',
  },
  {
    id: 4,
    descripcion: 'Finalizados',
  },
  {
    id: 5,
    descripcion: 'Cancelados',
  },
];

const SeguimientoDeTramites = () => {
  const history = useHistory();
  const screens = Grid.useBreakpoint();
  const user = useSelector(({ auth }) => auth.user);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones).filter((p) => p.id !== 5);
  const tiposDePasosDeTramites = useSelector(({ catalogs }) => catalogs.tiposDePasosDeTramites);
  const searchInputRef = useRef();
  const [formFilter] = Form.useForm();
  const tipoDeFiltro = Form.useWatch('tipoDeFiltro', formFilter);
  const filtro = Form.useWatch('filtro', formFilter);
  const nombre_solicitante = Form.useWatch('nombre_solicitante', formFilter);
  const apellido_solicitante = Form.useWatch('apellido_solicitante', formFilter);
  const folio = Form.useWatch('folio', formFilter);
  const estados_globales = Form.useWatch('estados_globales', formFilter);
  const tipo_de_paso = Form.useWatch('tipo_de_paso', formFilter);
  const usuario_puede_atender = Form.useWatch('usuario_puede_atender', formFilter);
  const pagados = Form.useWatch('pagados', formFilter);
  const padron = Form.useWatch('padron', formFilter);
  const object_id_padron = Form.useWatch('object_id_padron', formFilter);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ results: [] });

  const _isAdmin = useMemo(() => isAdmin(), []);

  const params = useMemo(() => {
    const _params = {};
    if (usuario_puede_atender) {
      _params.usuario_puede_atender = true;
    }
    if (pagados) {
      _params.pagados = true;
    }
    if (!_isAdmin) {
      if (user.centro_de_costos) {
        _params.unidad_de_recaudacion = user.unidad_de_recaudacion;
      } else if (user.unidad_responsable) {
        _params.departamento = user.unidad_responsable.id;
      } else if (user.unidad_operativa) {
        _params.dependencia = user.unidad_operativa.id;
      } else {
        return null;
      }
    }
    if (tipoDeFiltro && (filtro || padron || (nombre_solicitante && apellido_solicitante))) {
      if (tipoDeFiltro === 'padron') {
        _params.content_type_a_padron = getContenTypePorTipoDePadron(padron);
        _params.object_id_padron = object_id_padron;
      } else if (tipoDeFiltro === 'fecha_de_inicio') {
        _params[tipoDeFiltro] = filtro.format(DATE_FORMAT);
      } else if (tipoDeFiltro === 'nombre_solicitante') {
        _params.nombre_solicitante = nombre_solicitante;
        _params.apellido_solicitante = apellido_solicitante;
      } else if (filtro) {
        _params[tipoDeFiltro] = filtro;
      }
    }
    _params.estados_globales = estados_globales;
    _params.tipo_de_paso = tipo_de_paso;
    if (folio) {
      _params.folio = folio;
    }
    return _params;
  }, [folio, tipoDeFiltro, filtro, estados_globales, tipo_de_paso, usuario_puede_atender,
    nombre_solicitante, apellido_solicitante, pagados, padron, object_id_padron]);

  const [unidadesResponsables, setUnidadesResponsables] = useState([]);
  const [unidadesOperativas, setUnidadesOperativas] = useState([]);
  const [unidadesDeRecaudacion, setUnidadesDeRecaudacion] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [pasos, setPasos] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleModalFirmar, setVisibleModalFirmar] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [plazosMaximos, setPlazosMaximos] = useState([]);

  const focusSearchInput = () => setTimeout(() => searchInputRef.current?.focus());

  const fetchData = async (page = 1) => {
    setLoading(true);
    const filters = { ...params };
    if (filters.numero_de_identificacion_vehicular) {
      const [vehiculo] = await getVehiculoCaja({
        numero_de_identificacion_vehicular: filters.numero_de_identificacion_vehicular,
      });
      if (vehiculo) {
        filters.object_id_padron = vehiculo.id;
        filters.content_type_a_padron = getContenTypePorTipoDePadron(filtro);
      } else {
        message.warn('No se encontrón vehículo con NIV brindado');
        setLoading(false);
        return;
      }
    }
    const _data = await getTramitesIndexados({ ...filters, page });
    _data.page = page;
    const _historial = (await Promise.all(_data.results
      .map((e) => getHistorialDeTramite({ tramite: e.id })))).flat();
    const idsPlantillas = [...new Set(_data.results.map((e) => e.plantilla))];
    const _plazos = (await Promise.all(idsPlantillas
      .map((e) => getPlazosMaximos({ plantilla_de_tramites: e })))).flat();
    const _pasos = (await Promise.all(idsPlantillas
      .map((e) => getPasosDeTramites({ plantilla_de_tramite: e })))).flat();
    _data.results = _data.results.map((record) => {
      const lastLog = _historial.findLast((e) => e.tramite === record.id);
      const paso = lastLog || record.flujo_inicial_concluido ? _pasos.find((p) => {
        if (lastLog) return p.id === lastLog.paso_a_mandar;
        return record.plantilla === p.plantilla_de_tramite ? p.orden === 1 : false;
      }) : null;
      return { ...record, paso };
    });
    setData(_data);
    setHistorial(_historial);
    setPasos(_pasos);
    setPlazosMaximos(_plazos);
    setLoading(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      let _unidadesResponsables = [];
      let _unidadesOperativas = [];
      let _unidadesDeRecaudacion = [];
      let _usuarios = [];
      if ((!user.unidad_responsable && user.unidad_operativa) || _isAdmin) {
        _unidadesResponsables = await getDepartamentos(_isAdmin
          ? {} : { unidad_operativa: user.unidad_operativa?.id });
      } else {
        _unidadesResponsables = [user.unidad_responsable];
      }
      if (_isAdmin) {
        _unidadesOperativas = await getDependencias();
        _usuarios = await getUsuarios();
        _unidadesDeRecaudacion = await getUnidadesDeRecaudacion();
      }
      await getTiposDePasosDeTramites();
      setUsuarios(_usuarios);
      setUnidadesDeRecaudacion(_unidadesDeRecaudacion);
      setUnidadesResponsables(_unidadesResponsables);
      setUnidadesOperativas(_unidadesOperativas);
      focusSearchInput();
      if (params) {
        formFilter.submit();
      }
      setLoading(false);
    };
    if (!data.results.length) {
      fetch();
    }
  }, []);

  const tiposDeFiltros = [
    {
      id: 0,
      descripcion: 'Todo',
      visible: true,
    },
    {
      id: 'dependencia',
      descripcion: 'Dependencia',
      visible: _isAdmin,
    },
    {
      id: 'departamento',
      descripcion: 'Departamento',
      visible: _isAdmin || params?.dependencia,
    },
    {
      id: 'unidad_de_recaudacion',
      descripcion: 'Oficina',
      visible: _isAdmin,
    },
    {
      id: 'usuario_creo',
      descripcion: 'Usuario creo',
      visible: _isAdmin,
    },
    {
      id: 'padron',
      descripcion: 'Padrón',
      visible: true,
    },
    {
      id: 'nombre_solicitante',
      descripcion: 'Nombre de solicitante',
      visible: true,
    },
    {
      id: 'descripcion',
      descripcion: 'Trámite',
      visible: true,
    },
    {
      id: 'fecha_de_inicio',
      descripcion: 'Fecha de inicio',
      visible: true,
    },
  ].filter((e) => e.visible);

  const filtros = {
    0: null,
    usuario_creo: usuarios,
    unidad_de_recaudacion: unidadesDeRecaudacion,
    dependencia: unidadesOperativas,
    departamento: unidadesResponsables,
    nombre_solicitante: null,
    descripcion: null,
    fecha_de_inicio: null,
    numero_de_identificacion_vehicular: null,
  };

  const renderInfo = (_, record) => {
    const tipoPadronSolicitante = record.solicitante_ciudadano ? 1 : 15;
    const solicitante = record.solicitante_ciudadano ? record.ciudadano : record.contribuyente;
    const tipoDePadronAfectado = record.content_type_a_padron
      ? getTipoDePadronPorContenType(record.content_type_a_padron) : tipoPadronSolicitante;
    const departamento = unidadesResponsables.find((d) => d.id === record.departamento);
    return (
      <Row gutter={[10, 10]}>
        <LabelValue
          label="Folio"
          value={record.folio}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 4, md: 4 }}
        />
        <LabelValue
          label="Paso"
          value={record.paso ? `(${record.paso.orden}) ${record.paso.nombre}` : 'N/A'}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 5, md: 5 }}
        />
        <LabelValue
          label="Fecha"
          value={toMoment(record.fecha_de_inicio).format()}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 4, md: 4 }}
        />
        <Col span={5} />
        <LabelValue
          label="Trámite"
          value={record.descripcion}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 22, md: 22 }}
        />
        <LabelValue
          label="Solicitante"
          value={padronToString(solicitante, tipoPadronSolicitante)}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 10, md: 10 }}
        />
        <LabelValue
          label="Dependencia"
          value={departamento?.unidad_operativa?.descripcion}
          labelBps={{ xs: 3, md: 3 }}
          valueBps={{ xs: 9, md: 9 }}
        />
        <LabelValue
          label="Padrón"
          value={record.padron ? padronToString(record.padron, tipoDePadronAfectado) : 'Sin padrón'}
          labelBps={{ xs: 2, md: 2 }}
          valueBps={{ xs: 10, md: 10 }}
        />
        <LabelValue
          label="Departamento"
          value={departamento?.descripcion}
          labelBps={{ xs: 3, md: 3 }}
          valueBps={{ xs: 9, md: 9 }}
        />
      </Row>
    );
  };

  const Circle = ({ backgroundColor, borderColor }) => (
    <Col flex="40px">
      {backgroundColor && borderColor ? (
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor,
            color: '#FFFFFF',
            fontWeight: 'bold',
            fontSize: 18,
            border: `3px solid ${borderColor}`,
            marginBottom: 20,
          }}
        />
      ) : <center><Text style={{ marginBottom: 20, width: 40 }} strong>N/A</Text></center>}
    </Col>
  );

  const periodosMap = Object.freeze({
    DIAS_HABILES: 1,
    MESES: 2,
    ANIOS: 3,
    NA: 4,
  });

  const formatsMapByPeriod = {
    [periodosMap.DIAS_HABILES]: 'd',
    [periodosMap.MESES]: 'M',
    [periodosMap.ANIOS]: 'y',
  };

  const filteredToSign = data.params?.tipo_de_paso === tiposDePasosEnum.FIRMA_ELECTRONICA;
  const filteredEnProceso = (data.params?.estados_globales || estados_globales) === 2;

  const renderCircle = (record) => {
    const _plazosMaximos = plazosMaximos
      .filter((p) => p.plantilla_de_tramites === record.plantilla)
      .sort((a, b) => b.plazo_maximo - a.plazo_maximo);
    const [plazo] = _plazosMaximos;
    const unit = formatsMapByPeriod[plazo?.unidad_de_plazo_maximo];
    if (!unit) return <Circle />;
    const fechaInicio = toMoment(record.fecha_de_inicio);
    const expiryDate = toMoment(fechaInicio).add(plazo.plazo_maximo, unit);
    const diff = toMoment(new Date()).diff(expiryDate, 'days');
    if (diff < -1) {
      return <Circle backgroundColor="#59D948" borderColor="#2D9223" />;
    }
    if (diff > 0) {
      return <Circle backgroundColor="#E6242A" borderColor="#720A0D" />;
    }
    return <Circle backgroundColor="#FD6833" borderColor="#B44E2C" />;
  };

  const columns = [
    filteredEnProceso && {
      titleText: 'Semáforo / Porcentaje',
      width: 170,
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      render: (_, record) => {
        let percent = 'N/A';
        if (record.flujo_inicial_concluido) {
          const _pasos = pasos.filter((p) => p.plantilla_de_tramite === record.plantilla)
            .sort((a, b) => a.orden - b.orden);
          const log = [...historial].reverse().find((e) => e.tramite === record.id);
          const pasoActual = _pasos.find((p) => p.id === log?.paso);
          percent = (((100 / _pasos.length) * (pasoActual?.orden || 0)) || 0).toFixed(0);
        }

        return (
          <Row style={{ width: '100%' }}>
            {renderCircle(record)}
            <Col flex="30px" />
            <Col flex="60px">
              {percent === 'N/A'
                ? <center><Text style={{ textAlign: 'center' }}>N/A</Text></center>
                : (
                  <>
                    <center>
                      <Text style={{ textAlign: 'center', width: '100%' }}>
                        {percent}
                        %
                      </Text>
                    </center>
                    <Progress percent={percent} size="small" showInfo={false} trailColor="#ccc" />
                  </>
                )}
            </Col>
          </Row>
        );
      },
    },
    {
      titleText: 'Datos del trámite',
      width: 1100,
      dataIndex: 'info',
      key: 'info',
      render: renderInfo,
    },
  ].filter((c) => c);

  const rowSelection = {
    selectedRowKeys,
    type: filteredToSign ? 'checkbox' : 'radio',
    onChange: (_selectedRowKeys) => setSelectedRowKeys(_selectedRowKeys),
    getCheckboxProps: (record) => ({
      disabled: filteredToSign ? record.paso.firma_automatica : false,
    }),
  };

  const handleOnRowClick = (record) => {
    if (!filteredToSign) {
      setSelectedRowKeys([record.id]);
      return;
    }
    if (record.paso.firma_automatica) return;
    if (selectedRowKeys.includes(record.id)) {
      const clone = [...selectedRowKeys];
      clone.splice(selectedRowKeys.indexOf(record.id), 1);
      setSelectedRowKeys(clone);
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
    }
  };

  const selectedTramites = selectedRowKeys.map((s) => {
    const tramite = data.results.find((t) => t.id === s);
    const firmasElectronicas = JSON.parse(tramite.firma_electronica || '[]');
    tramite.firmable = tramite.paso && !firmasElectronicas
      .some((f) => !f.eliminado && f.paso === tramite.paso);
    return tramite;
  });

  const canSign = selectedRowKeys.length && selectedTramites.every((e) => e.firmable);

  if (!params) {
    return (
      <Row align="middle" justify="center" style={{ height: '100%' }}>
        <Result
          title="Sin trámites para mostrar"
          icon={<InboxOutlined className="warn" />}
          subTitle={<b>Usuario no cuenta con departamento o dependencia</b>}
        />
      </Row>
    );
  }

  const onClickEdit = () => {
    if (filteredToSign) {
      setVisibleModalFirmar(true);
    } else {
      const match = data.results.find((r) => r.id === selectedRowKeys[0]);
      history.push(`/tramites/registrar-tramite/?tramite=${match.id}`);
    }
  };

  const labelFiltro = tiposDeFiltros.find((tf) => tf.id === tipoDeFiltro)?.descripcion;

  return (
    <Spin tip="Cargando" spinning={loading}>
      <Form
        onFinish={() => fetchData()}
        form={formFilter}
        layout="vertical"
        initialValues={{
          tipoDeFiltro: 0,
          estados_globales: 2,
          usuario_puede_atender: !_isAdmin,
          pagados: false,
          padron: padrones[0]?.id,
        }}
        name="GPM"
        onValuesChange={(changed, allValues) => {
          if ('tipo_de_paso' in changed) {
            setTimeout(() => {
              formFilter.resetFields(['estados_globales']);
              formFilter.setFieldsValue({ usuario_puede_atender: true });
            });
          }
          if ('estados_globales' in changed) {
            if (allValues.usuario_puede_atender && changed.estados_globales !== 2) {
              setTimeout(() => formFilter.setFieldsValue({ usuario_puede_atender: false }));
            }
          }
          if ('usuario_puede_atender' in changed && !changed.usuario_puede_atender) {
            formFilter.resetFields(['tipo_de_paso']);
          }
          if (allValues.tipo_de_paso !== tiposDePasosEnum.FIRMA_ELECTRONICA
            && selectedRowKeys.length) {
            setSelectedRowKeys([]);
          }
        }}
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Búsqueda por folio"
              name="folio"
              tooltip="Búsqueda de trámite"
              normalize={capitalize}
            >
              <Input allowClear ref={searchInputRef} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Filtrar por" name="tipoDeFiltro" tooltip="Filtrado de trámites disponibles">
              <Select
                dataSource={tiposDeFiltros}
                onChange={() => formFilter.resetFields(['filtro'])}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8} hidden={!tipoDeFiltro || tipoDeFiltro === 'padron'}>
            {Array.isArray(filtros[tipoDeFiltro]) && (
              <Form.Item label={labelFiltro} name="filtro" rules={[{ required: true }]}>
                <Select
                  disabled={!tipoDeFiltro}
                  dataSource={filtros[tipoDeFiltro]}
                  allowClear={false}
                  render={tipoDeFiltro === 'usuario_creo' ? (e) => `${e.numero_de_empleado ? `${e.numero_de_empleado} - ` : ''}${e.nombre_completo}` : null}
                />
              </Form.Item>
            )}
            {tipoDeFiltro === 'fecha_de_inicio' && (
              <Form.Item label={labelFiltro} name="filtro" rules={[{ required: true }]}>
                <DatePicker
                  format="DD/MM/YYYY"
                  disabledDate={(date) => date.isAfter(new Date())}
                />
              </Form.Item>
            )}
            {['nombre_solicitante', 'descripcion', 'numero_de_identificacion_vehicular'].includes(tipoDeFiltro) && (
              <Form.Item
                label={tipoDeFiltro !== 'nombre_solicitante' ? labelFiltro : 'Nombre'}
                name={tipoDeFiltro !== 'nombre_solicitante' ? 'filtro' : 'nombre_solicitante'}
                rules={[{ required: true }]}
                normalize={toUpper}
              >
                <Input />
              </Form.Item>
            )}
          </Col>
          {tipoDeFiltro === 'padron' && (
            <InputSearchPro
              rules={[{ required: true }]}
              advancedComponent={BusquedaAvanzadaPadrones}
              advancedComponentProps={{ tipoDePadron: padron }}
              label={padrones.find((p) => p.id === padron)?.descripcion}
              resultProps={{
                name: 'object_id_padron',
                label: padrones.find((p) => p.id === padron)?.descripcion,
                keyProp: padron === 1 ? 'clave_ciudadana' : 'clave',
                render: (item) => padronToString(item, padron),
                allowClear: true,
                breakPoints: { md: 16 },
              }}
              prefix={{
                dataSource: padrones,
                rules: [{ required: true }],
                label: 'Padrón',
                allowClear: false,
                name: 'padron',
                style: { width: 150 },
              }}
              breakPoints={{ md: 16 }}
              getter={getPadronesMap[padron]}
            />
          )}
          {tipoDeFiltro === 'nombre_solicitante' && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label="Apellido"
                name="apellido_solicitante"
                rules={[{ required: true }]}
                normalize={toUpper}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Estado" name="estados_globales">
              <Select
                dataSource={estadosGlobalesDeTramites}
                allowClear={false}
                disabled={!!tipo_de_paso || usuario_puede_atender}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="usuario_puede_atender"
              label="Usuario puede atender"
              tooltip={(
                <>
                  Seleccione &quot;Si&quot; para ver los trámites por atender,
                  {' '}
                  {_isAdmin && 'selecione "No" para ver todos trámites'}
                  {!_isAdmin && `selecione "No" para ver los trámites de su ${user.unidad_operativa ? 'dependencia' : 'departamento'}`}
                </>
              )}
            >
              <Select trueFalse disabled={estados_globales !== 2} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="pagados"
              label="Solo pagados"
              tooltip={(
                <>
                  Seleccione &quot;Si&quot; para ver solo los trámites pagados,
                </>
              )}
            >
              <Select trueFalse />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="En tipo de paso" name="tipo_de_paso">
              <Select dataSource={tiposDePasosDeTramites} disabled={!usuario_puede_atender} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item style={{ marginTop: screens.md ? 22 : 0 }}>
              <Space>
                <Button htmlType="submit">
                  Filtrar
                  <FilterOutlined />
                </Button>
                <Button
                  onClick={() => setTimeout(() => formFilter.submit())}
                  htmlType="reset"
                >
                  Limpiar
                  <DeleteOutlined />
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <React.Fragment key={filteredEnProceso.toString()}>
        <Table
          data={data.results}
          cols={columns}
          handleOnRowClick={handleOnRowClick}
          rowSelection={rowSelection}
          allowSort={false}
          allowSearch={false}
          pagination={false}
          allowColumnSelector={false}
          paginationCallback={(page) => fetchData(page)}
          expandable={false}
          mobileColIndex={filteredEnProceso ? 1 : 0}
          count={data.count}
          currentPage={data.page}
          // restHeigth={screens.md ? 180 : 150}
          rowKey="id"
          emptyText={params ? null : 'Usuario no cuenta con departamento o dependencia'}
          editText={filteredToSign ? 'Firmar trámites' : 'Dar seguimiento'}
          controls={{ onClickEdit }}
          allowEdit
          permission={{
            change: filteredToSign ? canSign : true,
            view: true,
            hasAllViewPermission: true,
          }}
          hasAllViewPermission
        />
      </React.Fragment>
      <ModalFirmarTramites
        onCancel={() => setVisibleModalFirmar(false)}
        callback={(firmados) => {
          const failed = selectedRowKeys.filter((k) => !firmados.some((f) => f.id === k));
          setSelectedRowKeys(failed);
          if (failed.length) {
            message.warn('Falló el firmado de algunos trámites');
          } else {
            message.info('Trámites firmados correctamente');
          }
          fetchData();
        }}
        tramites={selectedTramites}
        visible={visibleModalFirmar}
        pasos={pasos}
      />
    </Spin>
  );
};

export default SeguimientoDeTramites;
